import * as React from 'react';

import type { PatientOutcomeSurface } from '@heidi/patients/contracts';

import type { SessionListLinkPopoverHost } from '../host';
import { PatientSearchCombobox } from '../patient-search-combobox/patient-search-combobox';
import type { SuggestedMatchCandidates } from '../suggested-matches';

interface SessionListLinkPopoverContentProps {
  /** Sessions to link the selected (or newly-created) patient to. */
  selectedSessionIds: string[];
  /** Closes the parent popover/drawer after a successful action. */
  setOpen: (open: boolean) => void;
  /**
   * Called when the user clicks Create new patient with no typed text.
   * The parent (`SessionListLinkPopover`) opens the legacy full-metadata
   * create modal — mounted there so it survives the popover closing.
   */
  onRequestEmptyCreate: () => void;
  /** Placeholder for the search input. */
  commandInputPlaceholder?: string;
  /** Disables interaction (e.g. read-only sessions). */
  disabled?: boolean;
  /** Candidate payload returned on the initiating session. */
  patientProfileAppointmentCandidates?: SuggestedMatchCandidates | null;
  host: SessionListLinkPopoverHost;
  /**
   * `patient_linked` surface attribution for this entry point (row avatar vs
   * bulk bar). Omitted by legacy callers — the event then fires bare.
   */
  trackingSurface?: PatientOutcomeSurface;
}

/**
 * Find-or-link content for the session-list link popover. A thin adapter:
 * `useSessionListLinkController` produces the props and the SAME
 * `PatientSearchCombobox` the in-session header uses renders them — in
 * `variant="popover"` mode. Keeping one component across both surfaces means
 * the search list, suggested matches, and the Create / Create-unlinked-session
 * footer can never drift between the header and the list.
 */
export const SessionListLinkPopoverContent: React.FC<
  SessionListLinkPopoverContentProps
> = ({
  selectedSessionIds,
  setOpen,
  onRequestEmptyCreate,
  commandInputPlaceholder,
  disabled = false,
  patientProfileAppointmentCandidates,
  trackingSurface,
  host,
}) => {
  const onClose = React.useCallback(() => setOpen(false), [setOpen]);
  const controller = host.useSessionListLinkController({
    selectedSessionIds,
    trackingSurface,
    patientProfileAppointmentCandidates,
    onRequestEmptyCreate,
    onClose,
    disabled,
  });

  return (
    <PatientSearchCombobox
      host={host.searchHost}
      variant="popover"
      placeholder={commandInputPlaceholder}
      data-testid="session-list-link-combobox"
      {...controller}
    />
  );
};
